import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, Select, Space, Typography } from 'antd';
import type { Dayjs } from 'dayjs';
import { PERIOD_PRESETS, presetRange, type PeriodPresetKey } from './periodPresets';

const { RangePicker } = DatePicker;

const PRESET_I18N: Record<PeriodPresetKey, string> = {
  this_month: 'thisMonth',
  last_month: 'lastMonth',
  this_quarter: 'thisQuarter',
  last_quarter: 'lastQuarter',
  ytd: 'ytd',
  last_year: 'lastYear',
  last_12: 'last12Months',
  custom: 'custom',
};

interface Props {
  value: [Dayjs, Dayjs];
  onChange: (range: [Dayjs, Dayjs]) => void;
  initialPreset?: PeriodPresetKey;
  label?: string;
}

/** Period preset + date range used by every period-based financial report. */
const PeriodRangeFilter: React.FC<Props> = ({ value, onChange, initialPreset = 'custom', label }) => {
  const { t } = useTranslation('common');
  const [preset, setPreset] = useState<PeriodPresetKey>(initialPreset);
  const options = useMemo(
    () =>
      PERIOD_PRESETS.map((p) => ({
        value: p.value,
        label: t(PRESET_I18N[p.value]),
      })),
    [t]
  );
  return (
    <Space direction="vertical" size={4}>
      <Typography.Text type="secondary">{label ?? t('period')}</Typography.Text>
      <Space.Compact>
        <Select
          style={{ width: 150 }}
          value={preset}
          options={options}
          onChange={(k) => {
            setPreset(k);
            const r = presetRange(k);
            if (r) onChange(r);
          }}
        />
        <RangePicker
          value={value}
          format="DD-MM-YYYY"
          allowClear={false}
          onChange={(d) => {
            if (d?.[0] && d?.[1]) {
              setPreset('custom');
              onChange([d[0], d[1]]);
            }
          }}
        />
      </Space.Compact>
    </Space>
  );
};

export default PeriodRangeFilter;
